import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Box, Grid, Typography, Paper, List, ListItemButton, ListItemText, Divider, useTheme } from '@mui/material'
import BasicInformationSection from './Sections/BasicInformationSection'
import ProfessionalDetailsSection from './Sections/ProfessionalDetailsSection'
import BackgroundInformationSection from './Sections/BackgroundInformationSection'
import PreferencesSection from './Sections/PreferencesSection'
import FileSection from './Sections/FileSection'
import AboutYouSection from './Sections/AboutYouSection'
import PasswordSection from './Sections/PasswordSection'
import { fetchUpdateCandidate } from 'src/redux/candidate'
import { fetchRegistrationData } from 'src/redux/supportingData'
import Loader from 'components/Loader'
import { NavigationBlockProvider } from './useGlobalNavigationBlock'

const CandidateProfile = () => {
  const user = useSelector(state => state.identity)
  const candidate = useSelector(state => state.candidate)
  const candidateTypes = useSelector(state => state.supportingData.candidateTypes)
  const dispatch = useDispatch()
  const [formData, setFormData] = useState({})
  const [loading, setLoading] = useState(true)
  const theme = useTheme()

  // Section anchor data
  const sections = [
    { label: 'File Uploads', id: 'file-uploads' },
    { label: 'Basic Information', id: 'basic-info' },
    { label: 'Professional Details', id: 'professional-details' },
    { label: 'Background Information', id: 'background-info' },
    { label: 'Preferences', id: 'preferences' },
    { label: 'About You', id: 'about-you' },
    { label: 'Password Reset', id: 'password' },
  ]

  useEffect(() => {
    dispatch(fetchRegistrationData())
  }, [])

  useEffect(() => {
    if (!candidate.loading) {
      setFormData({
        id: user.candidateId,
        phone: candidate.phone,
        city: candidate.city,
        state: candidate.state,
        showPhone: candidate.showPhone,
        showEmail: candidate.showEmail,
        email: candidate.email,
        searchStatus: candidate.searchStatus,
        availabilityDate: candidate.availabilityDate,
        resume: candidate.resume,
        residency: candidate.residency,
        residencyEnd: candidate.residencyEnd,
        about: candidate.about,
        aboutRaw: candidate.aboutRaw,
        visaIds: candidate.visas,
        candidateLanguages: candidate.candidateLanguages || [],
        specialtyIds: candidate.specialties,
        subspecialtyIds: candidate.subspecialties,
        npiNumber: candidate.npiNumber,
        boardEligibility: candidate.boardEligibility,
        licensedStates: candidate.licensedStates,
        candidateTypeId: candidate.candidateType?.id,
        yearsExperience: candidate.yearsExperience,
        preferredStates: candidate.preferredStates || [],
        areaTypePreference: candidate.areaTypePreference || '',
        preferredPracticeTypes: candidate.preferredPracticeTypes || [],
        relocationPreference: candidate.relocationPreference,
        aboutJob: candidate.aboutJob || '',
        preferredAreaTypes: candidate.preferredAreaTypes || [],
        academicPreference: candidate.academicPreference,
        preferredGlobalRegions: candidate.preferredGlobalRegions || [],
      })
      setLoading(false)
    }
  }, [candidate])

  const handleSaveSection = dataToSubmit => {
    dispatch(fetchUpdateCandidate(dataToSubmit, 'Profile Updated!'))
  }

  if (loading) {
    return <Loader />
  }

  return (
    <NavigationBlockProvider>
      {/* Profile Header */}
      <Box sx={{
        width: '100%',
        maxWidth: '100vw',
        overflowX: 'hidden',
        backgroundColor: theme.palette.background.dark,
        borderRadius: '0px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        mb: 4,
        pt: 0,
        pb: { xs: 3, md: 4 },
        px: 0,
      }}>
        <Box sx={{ maxWidth: '91.666667%', width: '100%', mx: 'auto', p: { xs: 2, md: 3 } }}>
          <Grid container spacing={0} alignItems="center">
            <Grid item xs={12}>
              <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold', mb: 1 }}>
                {candidate.firstName}'s Profile
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', mb: 0 }}>
                Manage your information, documents, and preferences
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Box>
      {/* End Profile Header */}
      <Box sx={{
        width: '100%',
        maxWidth: '100vw',
        minHeight: 'calc(100vh - 64px)',
        mt: '64px',
        background: theme.palette.background.default,
        overflowX: 'hidden'
      }}>
        <Grid container spacing={4} justifyContent="center" sx={{ width: '100%', maxWidth: { xs: '100%', md: '1400px' }, mx: 'auto', flexWrap: { xs: 'wrap', md: 'nowrap' }, overflowX: 'hidden' }}>
          {/* Sidebar */}
          <Grid item xs={12} md={4} sx={{ paddingLeft: '0px', paddingTop: '0px', display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-start' } }}>
            <Paper
              elevation={0}
              sx={{
                position: 'sticky',
                top: 84,
                background: theme.palette.background.paper,
                borderRadius: 2,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
                width: '100%',
                minWidth: { md: 270 },
                boxSizing: 'border-box',
                p: { xs: 1.5, md: 2.5 },
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
                }
              }}
            >
              <Box sx={{
                px: { xs: 2, md: 2.5 },
                py: { xs: 1.5, md: 2.5 },
                borderBottom: '1px solid',
                borderColor: 'divider',
                background: theme.palette.background.default
              }}>
                <Typography variant='h6' sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary
                }}>
                  Menu
                </Typography>
              </Box>
              <List sx={{ p: 0 }}>
                {sections.map((section, index) => (
                  <React.Fragment key={section.id}>
                    <ListItemButton
                      href={`#${section.id}`}
                      sx={{
                        py: 2,
                        px: 2.5,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          background: theme.palette.action.hover,
                          pl: 3
                        },
                      }}
                    >
                      <ListItemText
                        primary={section.label}
                        primaryTypographyProps={{
                          fontSize: '0.9375rem',
                          fontWeight: 500,
                          color: theme.palette.text.primary
                        }}
                      />
                    </ListItemButton>
                    {index < sections.length - 1 && (
                      <Divider sx={{ opacity: 0.5 }} />
                    )}
                  </React.Fragment>
                ))}
              </List>
            </Paper>
          </Grid>

          {/* Main Content */}
          <Grid item xs={12} md={8}>
            <Box>
              <Grid container spacing={4}>
                {sections.map((section) => (
                  <Grid item xs={12} key={section.id} id={section.id} p={0}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: { xs: 3, md: 4 },
                        background: theme.palette.background.paper,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
                        }
                      }}
                    >
                      <Typography
                        variant='h5'
                        sx={{
                          mb: 4,
                          fontWeight: 600,
                          color: theme.palette.text.primary
                        }}
                      >
                        {section.label}
                      </Typography>

                      {section.id === 'file-uploads' && (
                        <FileSection
                          formData={formData}
                          setFormData={setFormData}
                          onSave={handleSaveSection}
                          candidate={candidate}
                        />
                      )}
                      {section.id === 'basic-info' && (
                        <BasicInformationSection
                          formData={formData}
                          setFormData={setFormData}
                          onSave={handleSaveSection}
                          candidate={candidate}
                        />
                      )}
                      {section.id === 'professional-details' && (
                        <ProfessionalDetailsSection
                          formData={formData}
                          setFormData={setFormData}
                          onSave={handleSaveSection}
                          candidateTypes={candidateTypes}
                        />
                      )}
                      {section.id === 'background-info' && (
                        <BackgroundInformationSection
                          formData={formData}
                          setFormData={setFormData}
                          onSave={handleSaveSection}
                        />
                      )}
                      {section.id === 'preferences' && (
                        <PreferencesSection
                          formData={formData}
                          setFormData={setFormData}
                          onSave={handleSaveSection}
                        />
                      )}
                      {section.id === 'about-you' && (
                        <AboutYouSection
                          formData={formData}
                          setFormData={setFormData}
                          onSave={handleSaveSection}
                        />
                      )}
                      {section.id === 'password' && (
                        <PasswordSection candidate={candidate} />
                      )}
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </NavigationBlockProvider>
  )
}

export default CandidateProfile
